import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Building2, Plus, MapPin, Phone, Mail, Pencil, Loader2, ImageIcon, Upload, Trash2, Award } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", census_code: "", address: "", city: "", state: "", cep: "", phone: "", email: "", management_team: "", num_terms: 3, passing_grade: 5, status: "ATIVA", excellence_min_grade: 8, excellence_min_subjects_pct: 70, excellence_min_attendance: 80 };
const fmtCep = (v) => { const d = (v || "").replace(/\D/g, "").slice(0, 8); return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d; };

export default function Schools() {
  const { loadSchools, user } = useAuth();
  const [schools, setSchools] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const fetch = () => api.get("/schools").then((r) => setSchools(r.data));
  useEffect(() => { fetch(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty, ...s }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, num_terms: Number(form.num_terms), passing_grade: Number(form.passing_grade),
        excellence_min_grade: Number(form.excellence_min_grade), excellence_min_subjects_pct: Number(form.excellence_min_subjects_pct),
        excellence_min_attendance: Number(form.excellence_min_attendance) };
      if (editing) await api.put(`/schools/${editing.id}`, payload);
      else await api.post("/schools", payload);
      toast.success(editing ? "Escola atualizada" : "Escola cadastrada");
      setOpen(false);
      await fetch();
      await loadSchools(user);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (schools === null) return <div className="grid h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-blue-700" /></div>;

  return (
    <div>
      <PageHeader title={user?.role === "SUPERADMIN" ? "Escolas" : "Minha Escola"} subtitle={user?.role === "SUPERADMIN" ? "Unidades escolares da plataforma (multiescola)" : "Dados do estabelecimento, brasão e logo oficial"}>
        {user?.role === "SUPERADMIN" && <Button onClick={openNew} data-testid="school-create-button" className="bg-blue-700 hover:bg-blue-800">
          <Plus className="mr-1.5 h-4 w-4" /> Nova Escola
        </Button>}
      </PageHeader>

      {schools.length === 0 ? (
        <EmptyState icon={Building2} title="Nenhuma escola cadastrada" description="Cadastre a primeira unidade escolar." />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {schools.map((s) => (
            <Card key={s.id} data-testid="school-card" className="card-hover border-slate-200 p-5">
              <div className="flex items-start justify-between">
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-blue-700 text-white"><Building2 className="h-6 w-6" /></div>
                <StatusBadge status={s.status} />
              </div>
              <h3 className="mt-3 font-display text-lg font-bold text-slate-900">{s.name}</h3>
              {s.census_code && <p className="text-xs text-slate-400">Censo: {s.census_code}</p>}
              <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                {s.city && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {s.city}</p>}
                {s.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {s.phone}</p>}
                {s.email && <p className="flex items-center gap-2 truncate"><Mail className="h-4 w-4 text-slate-400" /> {s.email}</p>}
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                <span className="rounded bg-slate-100 px-2 py-1">{s.num_terms} trimestres</span>
                <span className="rounded bg-slate-100 px-2 py-1">Média {s.passing_grade}</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => openEdit(s)} data-testid={`school-edit-${s.id}`} className="mt-4 w-full">
                <Pencil className="mr-1.5 h-3.5 w-3.5" /> Editar
              </Button>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Editar Escola" : "Nova Escola"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Nome do estabelecimento *</Label><Input data-testid="school-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div><Label>Código/Censo (INEP)</Label><Input data-testid="school-census-input" value={form.census_code || ""} onChange={(e) => set("census_code", e.target.value)} /></div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_160px]">
              <div><Label>Regional (SEEDUC)</Label><Input data-testid="school-regional-input" placeholder="Ex.: METROPOLITANA IV" value={form.regional || ""} onChange={(e) => set("regional", e.target.value)} /></div>
              <div><Label>U.A.</Label><Input data-testid="school-ua-input" placeholder="Ex.: 180026" value={form.ua_code || ""} onChange={(e) => set("ua_code", e.target.value)} /></div>
            </div>
            <div><Label>Endereço completo</Label><Input data-testid="school-address-input" placeholder="Rua, número, complemento, bairro" value={form.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_90px_130px]">
              <div><Label>Município</Label><Input data-testid="school-city-input" value={form.city || ""} onChange={(e) => set("city", e.target.value)} /></div>
              <div><Label>UF</Label><Input data-testid="school-state-input" maxLength={2} value={form.state || ""} onChange={(e) => set("state", e.target.value.toUpperCase())} /></div>
              <div><Label>CEP</Label><Input data-testid="school-cep-input" placeholder="00000-000" value={form.cep || ""} onChange={(e) => set("cep", fmtCep(e.target.value))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Telefone</Label><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
              <div><Label>E-mail</Label><Input value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
            </div>
            <div><Label>Equipe gestora</Label><Textarea rows={2} value={form.management_team || ""} onChange={(e) => set("management_team", e.target.value)} /></div>
            <div className="rounded-lg bg-slate-50 p-3" data-testid="school-signatures-section">
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Assinaturas do Histórico Escolar</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_160px]">
                <div><Label>Secretário(a) Escolar — Nome</Label><Input data-testid="school-secretary-name-input" value={form.secretary_name || ""} onChange={(e) => set("secretary_name", e.target.value)} /></div>
                <div><Label>Matrícula</Label><Input data-testid="school-secretary-registration-input" value={form.secretary_registration || ""} onChange={(e) => set("secretary_registration", e.target.value)} /></div>
                <div><Label>Diretor(a) — Nome</Label><Input data-testid="school-director-name-input" value={form.director_name || ""} onChange={(e) => set("director_name", e.target.value)} /></div>
                <div><Label>Matrícula</Label><Input data-testid="school-director-registration-input" value={form.director_registration || ""} onChange={(e) => set("director_registration", e.target.value)} /></div>
              </div>
              <p className="mt-2 text-xs text-slate-500">Em caso de troca de servidor, basta editar. Os próximos históricos já sairão com os novos dados.</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Configurações Acadêmicas</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nº de trimestres</Label><Input type="number" value={form.num_terms} onChange={(e) => set("num_terms", e.target.value)} /></div>
                <div><Label>Média escolar (0 a 10)</Label><Input type="number" step="0.1" min={0} max={10} data-testid="school-passing-grade-input" value={form.passing_grade} onChange={(e) => set("passing_grade", e.target.value)} /></div>
              </div>
              <p className="mb-2 mt-3 text-xs font-bold uppercase tracking-wider text-slate-400">Critérios de Excelência</p>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs">Nota mín. (0–10)</Label><Input type="number" step="0.1" min={0} max={10} data-testid="school-excellence-grade-input" value={form.excellence_min_grade} onChange={(e) => set("excellence_min_grade", e.target.value)} /></div>
                <div><Label className="text-xs">% disciplinas</Label><Input type="number" value={form.excellence_min_subjects_pct} onChange={(e) => set("excellence_min_subjects_pct", e.target.value)} /></div>
                <div><Label className="text-xs">Freq. acima de (%)</Label><Input type="number" value={form.excellence_min_attendance} onChange={(e) => set("excellence_min_attendance", e.target.value)} /></div>
              </div>
            </div>
            {editing ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <ImageSection schoolId={editing.id} kind="brasao" title="Brasão oficial" has={!!editing.brasao_path} />
                <ImageSection schoolId={editing.id} kind="logo" title="Logo oficial" has={!!editing.logo_path} />
              </div>
            ) : <p className="text-xs text-slate-500">Salve a escola para enviar o brasão, o logo e o fundo do certificado.</p>}
            {editing && (
              <CertBackgroundSection schoolId={editing.id} hasBg={!!editing.certificate_bg_path} />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.name} data-testid="school-save-button" className="bg-blue-700 hover:bg-blue-800">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ImageSection({ schoolId, kind, title, has }) {
  const [exists, setExists] = useState(has);
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(Date.now());
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/schools/${schoolId}/image/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setExists(true); setBust(Date.now()); toast.success(`${title} atualizado`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); e.target.value = ""; }
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/schools/${schoolId}/image/${kind}`); setExists(false); toast.success(`${title} removido`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg bg-slate-50 p-3" data-testid={`school-${kind}-section`}>
      <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">{title}</p>
      <div className="mb-2 grid h-28 place-items-center overflow-hidden rounded-lg border border-dashed border-slate-300 bg-white">
        {exists
          ? <img src={`${BACKEND}/api/schools/${schoolId}/image/${kind}?t=${bust}`} alt={title} className="max-h-24 max-w-full object-contain" data-testid={`school-${kind}-preview`} />
          : <span className="flex items-center gap-1.5 text-xs text-slate-400"><ImageIcon className="h-4 w-4" /> Não enviado</span>}
      </div>
      <div className="flex gap-2">
        <label className="inline-flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid={`school-${kind}-upload`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{exists ? "Trocar" : "Enviar"}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} disabled={busy} data-testid={`school-${kind}-file-input`} />
        </label>
        {exists && <Button type="button" variant="outline" size="sm" onClick={remove} disabled={busy} data-testid={`school-${kind}-remove`} className="text-rose-600 hover:text-rose-700"><Trash2 className="h-4 w-4" /></Button>}
      </div>
      <p className="mt-1 text-[11px] text-slate-400">PNG, JPG ou WEBP, máx. 8MB (fundo transparente recomendado).</p>
    </div>
  );
}

function CertBackgroundSection({ schoolId, hasBg }) {
  const [exists, setExists] = useState(hasBg);
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(Date.now());
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/schools/${schoolId}/certificate-background`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setExists(true);
      setBust(Date.now());
      toast.success("Fundo do certificado atualizado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/schools/${schoolId}/certificate-background`);
      setExists(false);
      toast.success("Fundo removido");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg bg-slate-50 p-3" data-testid="cert-background-section">
      <p className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">
        <Award className="h-3.5 w-3.5" /> Fundo do Certificado
      </p>
      <p className="mb-3 text-xs text-slate-500">
        Imagem de fundo aplicada aos certificados desta escola (paisagem, PNG/JPG/WEBP, máx. 8MB). Um véu branco é aplicado para manter o texto legível.
      </p>
      {exists ? (
        <div className="relative mb-3 overflow-hidden rounded-lg border border-slate-200 bg-white">
          <img src={`${BACKEND}/api/schools/${schoolId}/certificate-background?t=${bust}`}
            alt="Fundo do certificado" className="h-36 w-full object-cover" data-testid="cert-background-preview" />
        </div>
      ) : (
        <div className="mb-3 grid h-24 place-items-center rounded-lg border border-dashed border-slate-300 bg-white text-xs text-slate-400">
          <span className="flex items-center gap-1.5"><ImageIcon className="h-4 w-4" /> Nenhum fundo personalizado</span>
        </div>
      )}
      <div className="flex gap-2">
        <label className="inline-flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="cert-background-upload">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {exists ? "Trocar imagem" : "Enviar imagem"}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} disabled={busy} />
        </label>
        {exists && (
          <Button type="button" variant="outline" size="sm" onClick={remove} disabled={busy}
            data-testid="cert-background-remove" className="text-rose-600 hover:text-rose-700">
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
